// Image crop modal — Cropper.js (via react-cropper) running on the full
// original image in a normal DOM overlay. We read ONLY the crop rectangle
// (cropper.getData, in natural image px) and hand it back to the Toolbar,
// which applies it as Fabric's NATIVE crop (cropX/cropY/width/height) on the
// source object — NOT a clipPath. A clipPath only masks, leaving the object
// full-size with the cropped area as whitespace; native crop shrinks the
// object's box/footprint and serializes via Image.toObject. No pixel
// extraction happens here, so it also works for CORS-tainted remote images.
// See applyCropFromModal in StudioCoreEditor.jsx.
//
// This replaces the old hand-rolled in-canvas crop engine, which fought
// Fabric's selection/event model (skipTargetFind → empty-target clicks →
// discardActiveObject → crop torn down). A DOM cropper sidesteps all of that.
import { useRef, useState } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconX } from './toolbarIcons';

const RATIOS = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '16:9', value: 16 / 9 },
];

export function ImageCropModal({ src, initialData, onApply, onCancel }) {
  const { t } = useTranslation('editor');
  const cropperRef = useRef(null);
  const [aspect, setAspect] = useState(null); // null = free

  // Cropper is ready: seed the crop box from any existing clipPath so
  // re-cropping starts from the current crop instead of the whole image.
  const handleReady = () => {
    const c = cropperRef.current;
    if (c && initialData) {
      try {
        c.setData(initialData);
      } catch {
        /* fall back to the default full-image crop box */
      }
    }
  };

  const chooseRatio = (value) => {
    setAspect(value);
    const c = cropperRef.current;
    if (c) c.setAspectRatio(value == null ? NaN : value);
  };

  const handleApply = () => {
    const c = cropperRef.current;
    if (!c) {
      onCancel();
      return;
    }
    // {x, y, width, height, rotate, scaleX, scaleY} in NATURAL image pixels,
    // which map 1:1 onto the Fabric image's local (width/height) space.
    onApply(c.getData(true));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className="cce-crop-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="cce-crop-modal"
        role="dialog"
        aria-label={t('crop.title')}
        tabIndex={-1}
        ref={(el) => el && el.focus()}
        onKeyDown={onKeyDown}
      >
        <div className="cce-crop-modal-head">
          <span className="cce-crop-modal-title">{t('crop.title')}</span>
          <div className="cce-crop-ratios">
            {RATIOS.map((r) => (
              <button
                key={r.label}
                type="button"
                className={`cce-crop-ratio${aspect === r.value ? ' active' : ''}`}
                onClick={() => chooseRatio(r.value)}
              >
                {r.value === null ? t('crop.ratioFree') : r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cce-crop-modal-body">
          <div className="cce-crop-canvas">
            <Cropper
              src={src}
              style={{ height: '100%', width: '100%' }}
              viewMode={1}
              dragMode="crop"
              autoCropArea={1}
              background
              responsive
              checkOrientation={false}
              guides
              center
              highlight
              toggleDragModeOnDblclick={false}
              ready={handleReady}
              onInitialized={(instance) => {
                cropperRef.current = instance;
              }}
            />
          </div>
        </div>

        <div className="cce-crop-modal-foot">
          <span className="cce-crop-modal-hint">{t('crop.hint')}</span>
          <div className="cce-crop-modal-actions">
            <button type="button" className="cce-crop-btn cancel" onClick={onCancel}>
              <IconX width={13} height={13} /> {t('common:actions.cancel')}
            </button>
            <button type="button" className="cce-crop-btn apply" onClick={handleApply}>
              <IconCheck width={13} height={13} /> {t('crop.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
